import Swal, {
  type SweetAlertOptions,
  type SweetAlertResult,
} from "sweetalert2";

const themeColorMap = {
  primary: { bg: "--primary", fg: "--primary-foreground" },
  secondary: { bg: "--secondary", fg: "--secondary-foreground" },
  destructive: { bg: "--destructive", fg: "--destructive-foreground" },
  outline: { bg: "--border", fg: "--foreground" },
  ghost: { bg: "--accent", fg: "--accent-foreground" },
} as const;

export type SwalThemeColor = keyof typeof themeColorMap;

function resolveCssVar(varName: string): string {
  if (typeof document === "undefined") return "";
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return `hsl(${value})`;
}

function resolveButtonColor(
  value: string | undefined,
): { bg: string; fg?: string } | null {
  if (!value) return null;
  if (value in themeColorMap) {
    const theme = themeColorMap[value as SwalThemeColor];
    return {
      bg: resolveCssVar(theme.bg),
      fg: resolveCssVar(theme.fg),
    };
  }
  return { bg: value };
}

export type AppSweetAlertOptions = Omit<
  SweetAlertOptions,
  "confirmButtonColor" | "cancelButtonColor" | "denyButtonColor"
> & {
  confirmButtonColor?: SwalThemeColor | string;
  cancelButtonColor?: SwalThemeColor | string;
  denyButtonColor?: SwalThemeColor | string;
};

const swalMixin = Swal.mixin({
  showConfirmButton: true,
  confirmButtonText: "Aceptar",
  cancelButtonText: "Cancelar",
  reverseButtons: false,
  /**
   * `topLayer` (Popover nativo) deja un ::backdrop que se come los clics.
   * `animation: false` evita esperar `animationend` que a veces no dispara.
   */
  animation: false,
  heightAuto: false,
  scrollbarPadding: false,
  returnFocus: false,
  allowOutsideClick: true,
  allowEscapeKey: true,
});

function unlockSwalPointerEvents(popup: HTMLElement): void {
  const container = popup.closest<HTMLElement>(".swal2-container");
  if (container) {
    container.style.setProperty("pointer-events", "auto", "important");
    if (container.hasAttribute("popover")) {
      container.setAttribute("popover", "manual");
    }
  }
  popup.style.setProperty("pointer-events", "auto", "important");
}

export async function showSwal(
  options: AppSweetAlertOptions,
): Promise<SweetAlertResult> {
  const {
    confirmButtonColor,
    cancelButtonColor,
    denyButtonColor,
    didRender: userDidRender,
    didOpen: userDidOpen,
    ...rest
  } = options;

  const confirm =
    resolveButtonColor(confirmButtonColor) ?? resolveButtonColor("primary")!;
  const cancel =
    resolveButtonColor(cancelButtonColor) ?? resolveButtonColor("outline")!;
  const deny =
    resolveButtonColor(denyButtonColor) ?? resolveButtonColor("destructive")!;

  const mergedDidRender = (popup: HTMLElement) => {
    unlockSwalPointerEvents(popup);
    if (confirm.fg) {
      const btn = popup.querySelector<HTMLElement>(".swal2-confirm");
      if (btn) btn.style.color = confirm.fg;
    }
    if (cancel?.fg) {
      const btn = popup.querySelector<HTMLElement>(".swal2-cancel");
      if (btn) btn.style.color = cancel.fg;
    }
    if (deny?.fg) {
      const btn = popup.querySelector<HTMLElement>(".swal2-deny");
      if (btn) btn.style.color = deny.fg;
    }
    userDidRender?.(popup);
  };

  const mergedDidOpen = (popup: HTMLElement) => {
    unlockSwalPointerEvents(popup);
    userDidOpen?.(popup);
  };

  return swalMixin.fire({
    ...rest,
    confirmButtonColor: confirm.bg,
    cancelButtonColor: cancel?.bg,
    denyButtonColor: deny?.bg,
    didRender: mergedDidRender,
    didOpen: mergedDidOpen,
  } as SweetAlertOptions);
}

/** Feedback de sesión que no bloquea clics ni navegación. */
export function showSwalToast(
  options: AppSweetAlertOptions,
): Promise<SweetAlertResult> {
  return showSwal({
    toast: true,
    position: "top-end",
    timer: 4000,
    timerProgressBar: true,
    showConfirmButton: false,
    ...options,
  });
}
